import { PoiType } from '@/enums/floor-guide';
import { useMarker } from '@/hooks/map';
import { TrackingService } from '@/services/analytics/TrackingService';
import { useConfigStore } from '@/stores';
import { useMarkerStore } from '@/stores/markerStore';
import type { NavigationGuideEndPoint } from '@/types/floor-guide';
import type { RoutePoint } from '@/types/route-simulation';
import { calculateEndPointFromPoi } from '@/utils/map-poi';
import { getPoiBuildingName } from '@/utils/shop';
import { generateNavigationOptions, generateWalkingOptions } from '@/utils/route-simulation';
import type { DabeeoMap, DabeeoMapData } from 'dabeeomaps';
import type { IPoi } from 'dabeeomaps/dist/src/model/map/IPoi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCalculateIconSize } from './useCalculateIconSize';
import { usePoiDetail } from './usePoiDetail';
import type { IClosestPoiDataOption } from 'dabeeomaps/dist/src/model/IDataOption';
import useTickSound from '../useTickSound';

type ModalState = {
    shopPoi: boolean;
    fnbPoi: boolean;
    facilityPoi: boolean;
    contentPoi: boolean;
    noPoiContent: boolean;
};

const initialModalState: ModalState = {
    shopPoi: false,
    fnbPoi: false,
    facilityPoi: false,
    contentPoi: false,
    noPoiContent: false,
};

export const usePoiContentState = (mapData?: DabeeoMapData, currentMap?: DabeeoMap) => {
    const server = useConfigStore((state) => state.config?.server);
    const screenMode = useConfigStore((state) => state.config?.ui?.screenMode);
    const language = useConfigStore((state) => state.config?.ui?.language);
    const buildings = useConfigStore((state) => state.config?.server?.store?.buildings);
    const { playTick } = useTickSound();

    const { markerIds, setMarkerIds } = useMarkerStore();

    const { clearMarker } = useMarker(currentMap);

    const { handleCheckPoiType, handleGetPoiItemInAllBuildings } = usePoiDetail();
    const { calculateIconSize } = useCalculateIconSize();

    const [modalState, setModalState] = useState<ModalState>(structuredClone(initialModalState));
    const [endPoint, setEndPoint] = useState<RoutePoint | undefined>(undefined);
    const [routes, setRoutes] = useState<any>(undefined);
    const [selectedRoute, setSelectedRoute] = useState<any | undefined>(undefined);
    const [selectedEndPointId, setSelectedEndPointId] = useState<string | undefined>(undefined);
    const [selectedContentId, setSelectedContentId] = useState<string | undefined>(undefined);

    const startPoint = useMemo<RoutePoint>(() => {
        return {
            floorId: server?.floor?.floorCode ?? '',
            floorName: server?.floor?.name['ko'] ?? '',
            position: {
                x: server?.machine?.positionX ?? 0,
                y: server?.machine?.positionY ?? 0,
            },
        };
    }, [server?.floor, server?.machine]);

    const currentPoiOptions = useMemo<IClosestPoiDataOption>(() => {
        return {
            floorId: server?.floor?.floorCode ?? '',
            position: {
                x: server?.machine?.positionX ?? 0,
                y: server?.machine?.positionY ?? 0,
            },
            // categoryCode: Array.isArray(poi?.categoryCode) ? poi?.categoryCode[0] : poi?.categoryCode,
        };
    }, [server?.machine?.positionX, server?.machine?.positionY, server?.floor?.floorCode]);

    // endPoint is now direct state, no need for useMemo

    const allPoiContents = useMemo(() => {
        if (!Array.isArray(buildings) || !buildings?.length) {
            return [];
        }

        return buildings.flatMap((building) => (Array.isArray(building.poiContents) ? building.poiContents : []));
    }, [buildings]);

    const poiItemBySelectedEndPointId = useMemo(() => {
        if (selectedContentId) {
            const contentItem = allPoiContents.find((poi) => poi.id === selectedContentId);
            if (contentItem) return contentItem;
        }
        return handleGetPoiItemInAllBuildings(selectedEndPointId);
    }, [selectedEndPointId, selectedContentId, handleGetPoiItemInAllBuildings, allPoiContents]);

    const navigationOptions = useMemo(() => {
        return generateNavigationOptions(
            server?.store?.mockRoutingLineWeight,
            calculateIconSize({ width: 26, height: 26 }),
            calculateIconSize({ width: 36, height: 56 })
        );
    }, [server?.store?.mockRoutingLineWeight, calculateIconSize]);

    const generateEndpointFloorName = useMemo(() => {
        return endPoint?.floorName ?? '';
    }, [endPoint?.floorName]);

    const storeCode = server?.store?.storeCode ?? null;

    const generateNavigationGuideEndPoint = useMemo<NavigationGuideEndPoint>(() => {
        const selectedPoiContent = selectedContentId
            ? allPoiContents.find((poi) => poi.id === selectedContentId) ?? allPoiContents.find((poi) => poi.studioPoiId === selectedEndPointId)
            : allPoiContents.find((poi) => poi.studioPoiId === selectedEndPointId);

        const selectedBuilding =
            Array.isArray(buildings) && buildings?.length > 0
                ? buildings?.find((building) => building?.studioBuildingId === selectedPoiContent?.studioBuildingId)
                : null;
        const defaultName = selectedBuilding ? selectedBuilding?.name?.[language] || selectedBuilding?.name?.['ko'] || '' : '';
        const buildingName = selectedPoiContent ? getPoiBuildingName(selectedPoiContent, language, defaultName, storeCode) : defaultName;

        const selectedPoiName = selectedPoiContent?.name?.[language] || selectedPoiContent?.name?.['ko'] || '';

        return {
            floorName: generateEndpointFloorName,
            buildingName,
            poiName: selectedPoiName,
        };
    }, [generateEndpointFloorName, language, buildings, allPoiContents, selectedEndPointId, selectedContentId, storeCode]);

    const walkingOptions = useMemo(() => {
        const zoom = currentMap?.context?.getMapOptions?.()?.controlOption?.zoom;
        return generateWalkingOptions(server?.store?.mockRoutingOption, server?.store?.mockRoutingSpeed, zoom, calculateIconSize({ width: 42, height: 42 }));
    }, [server?.store?.mockRoutingSpeed, server?.store?.mockRoutingOption, currentMap, calculateIconSize]);

    const handleSetModalState = useCallback((modal: Partial<ModalState>) => {
        setModalState((prev) => ({
            ...prev,
            ...modal,
        }));
    }, []);

    /**
     * Set endpoint from POI - works for both async and immediate use cases
     * Since endPoint is now direct state, no timing issues!
     *
     * @param poi - POI object to convert to endpoint
     */
    const setEndPointFromPoi = useCallback(
        (poi: IPoi | any): void => {
            const calculatedEndPoint = calculateEndPointFromPoi(poi, mapData);
            setEndPoint(calculatedEndPoint);
            setSelectedEndPointId(poi?.id);
        },
        [mapData]
    );

    const handlePoiClick = useCallback(
        (data: any) => {
            if (!Array.isArray(data) || !data?.length || !data[0]?.id) {
                return;
            }

            playTick('ting');
            const poiContent = handleGetPoiItemInAllBuildings(data[0].id);

            if (poiContent) {
                TrackingService.trackPoi(poiContent?.id ?? data[0].id, undefined, poiContent);
            }

            const poiList = Array.isArray(markerIds?.poiList) ? markerIds?.poiList : [];
            const selectedPoiMarkerId = markerIds?.selectedPoi ? [markerIds?.selectedPoi] : [];
            clearMarker([...poiList, ...selectedPoiMarkerId]);
            setMarkerIds({ selectedPoi: undefined, poiList: undefined });

            setEndPointFromPoi(data[0]);

            const poiType = handleCheckPoiType(data[0].id);

            if (poiType === PoiType.FACILITY) {
                handleSetModalState({ facilityPoi: true });
                return;
            }

            if (poiType === PoiType.SHOP) {
                handleSetModalState({ shopPoi: true });
                return;
            }

            if (poiType === PoiType.FNB) {
                handleSetModalState({ fnbPoi: true });
                return;
            }

            if (poiType === PoiType.CONTENT) {
                handleSetModalState({ contentPoi: true });
                return;
            }

            // handleSetModalState({ noPoiContent: true });
        },
        [handleCheckPoiType, handleSetModalState, clearMarker, setMarkerIds, markerIds, setEndPointFromPoi]
    );

    const handleResetModalState = useCallback(() => {
        setModalState(structuredClone(initialModalState));
    }, []);

    const handleResetState = useCallback(() => {
        setModalState(structuredClone(initialModalState));
        setEndPoint(undefined);
        setRoutes(undefined);
        setSelectedRoute(undefined);
        setSelectedEndPointId(undefined);
        setSelectedContentId(undefined);
    }, []);

    useEffect(() => {
        return () => {
            handleResetState();
        };
    }, [handleResetState]);

    useEffect(() => {
        handleResetState();
    }, [screenMode, handleResetState]);

    return {
        modalState,
        startPoint,
        endPoint,
        navigationOptions,
        walkingOptions,
        routes,
        selectedRoute,
        selectedEndPointId,
        selectedContentId,
        poiItemBySelectedEndPointId,
        generateEndpointFloorName,
        generateNavigationGuideEndPoint,
        currentPoiOptions,
        setRoutes,
        setSelectedRoute,
        handleSetModalState,
        handleResetModalState,
        handlePoiClick,
        setSelectedEndPointId,
        setSelectedContentId,
        setEndPoint,
        setEndPointFromPoi,
    };
};
